import { createContext, useEffect, useState } from 'react';
import { extendTheme, ChakraProvider } from '@chakra-ui/react';
import { bodyFont, colours } from 'constants/theme';
import { useFetchSettings } from 'hooks';
import {
  ButtonStyles,
  CardStyles,
  CardTitleStyles,
  CheckboxStyles,
  LinkStyles,
  SpinnerStyles,
  InputStyles,
  FormLabelStyles,
  IconStyles,
  TextStyles,
  HeadingStyles,
  ProgressStyles,
  TabsStyles,
  CloseButtonStyles,
  ModalStyles,
  IconButtonStyles,
  SwitchStyles,
  TextareaStyles,
  ListBoxStyles,
  AccordionIconStyles,
  AccordionButtonStyles,
} from 'components/UI';
import { IColours, ISettings } from 'types';
import { checkIfError } from 'utils/helpers';
import { LinkButtonStyles } from 'components/common/Buttons/LinkButton';
import settingsMockData from 'fixtures/settings/settings.json';
import { reduceSettings } from 'utils/settings';

export type ISettingsProperty = keyof ISettings;

type ThemerProps = {
  children: React.ReactNode;
  storybook?: boolean;
};

const ThemeContext = createContext<ISettings | null>(null);

const formatColours = (palette: IColours | null) =>
  palette
    ? {
        primaryThemeColour: {
          400: palette.primary1,
          500: palette.primary2,
        },
        secondaryThemeColour: {
          400: palette.secondary1,
          500: palette.secondary2,
        },
        tertiaryThemeColour: {
          400: palette.tertiary1,
          500: palette.tertiary2,
        },
        base1Colour: {
          400: palette.base1Colour1,
          500: palette.base1Colour2,
        },
        base2Colour: {
          400: palette.base2Colour1,
          500: palette.base2Colour2,
        },
        primaryFontColour: {
          400: palette.primaryText,
          500: palette.primaryText2,
        },
        secondaryFontColour: {
          400: palette.secondaryText,
          500: palette.secondaryText2,
        },
        base1FontColour: {
          400: palette.base1ColourText,
          500: palette.base1ColourText2,
        },
        base2FontColour: {
          400: palette.base2ColourText,
          500: palette.base2ColourText2,
        },
        scrollbarThumbColour: {
          400: palette.scrollbarThumbColour,
        },
      }
    : {};

const ThemeProvider = ({ children, storybook = false }: ThemerProps) => {
  const [fetchSettings] = useFetchSettings();
  const [settings, setSettings] = useState<ISettings | null>(null);

  const setCSSVariables = (theme: ISettings) => {
    colours.forEach((colour: string) => {
      if (!(colour in theme)) return;
      const themeColour = theme[colour as ISettingsProperty] ?? '';

      document?.documentElement?.style?.setProperty(`--${colour}`, themeColour as string);

      // make the gradient and add to the document
      if ((themeColour as string).includes('#')) {
        let hex = themeColour;
        const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
        hex = (hex as string).replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);

        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

        if (!result || !Array.isArray(result)) return;

        const rgb = {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        };

        const rgbName = `${colour}Hex`;
        const rgbValue = `rgba(${rgb.r},${rgb.g},${rgb.b},1)`;
        document?.documentElement?.style?.setProperty(`--${rgbName}`, rgbValue);
      }
    });
  };

  const fetchData = async () => {
    const settingsResult = await fetchSettings();

    if (checkIfError(settingsResult)) return;

    setCSSVariables(settingsResult);
    if (settingsResult) {
      setSettings(settingsResult);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const environmentSettings = storybook
    ? (reduceSettings(settingsMockData) as Record<keyof ISettings, string | string[]> as ISettings | null)
    : settings;

  const colors = formatColours(environmentSettings);

  const variantTheme = extendTheme({
    colors,
    shadows: {
      outline: '0 0 0 3px rgba(0, 0, 0, 0.6)',
    },
    fontSizes: {
      sm: '0.75rem',
      md: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.625rem',
      '4xl': '2rem',
      '5xl': '2.25rem',
      overlayTitle: '1.125rem',
      subTitle: '1.625rem',
      title: '2.25rem',
    },
    components: {
      AccordionIcon: AccordionIconStyles,
      AccordionButton: AccordionButtonStyles,
      CloseButton: CloseButtonStyles,
      Button: ButtonStyles,
      CardTitle: CardTitleStyles,
      Card: CardStyles,
      Checkbox: CheckboxStyles,
      Link: LinkStyles,
      Text: TextStyles,
      Spinner: SpinnerStyles,
      Input: InputStyles,
      FormLabel: FormLabelStyles,
      Icon: IconStyles,
      IconButton: IconButtonStyles,
      Heading: HeadingStyles,
      Progress: ProgressStyles,
      Tabs: TabsStyles,
      Modal: ModalStyles,
      Switch: SwitchStyles,
      Textarea: TextareaStyles,
      LinkButton: LinkButtonStyles,
      Select: ListBoxStyles,
    },
    styles: {
      global: {
        'html, body': {
          fontFamily: bodyFont,
        },
        '::-webkit-scrollbar': {
          w: 1.5,
        },
        '::-webkit-scrollbar-track': {
          borderRadius: 5,
          bgColor: 'base2Colour.400',
        },
        '::-webkit-scrollbar-thumb': {
          borderRadius: 5,
          bgColor: 'scrollbarThumbColour.400',
        },
        '.grecaptcha-badge': {
          visibility: 'hidden',
        },
      },
    },
  });

  return (
    <ChakraProvider theme={variantTheme}>
      <ThemeContext.Provider value={environmentSettings}>{children}</ThemeContext.Provider>
    </ChakraProvider>
  );
};

export { ThemeProvider, ThemeContext };
